import React, { useEffect, useMemo, useState } from 'react';
import {
  Edit2,
  MapPin,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Truck
} from 'lucide-react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch
} from 'firebase/firestore/lite';
import { db, ensureFirebaseAuth } from '../../firebase';
import './AdminServiceAreas.css';

const SERVICE_AREAS_COLLECTION = 'serviceAreas';
const FIRESTORE_TIMEOUT_MS = 12000;

const emptyForm = {
  name: '',
  city: '',
  state: '',
  status: 'working',
  message: '',
  latitude: '',
  longitude: '',
  radiusKm: '',
  sortOrder: '0',
  isActive: true
};

const defaultMessages = {
  working: 'Service is available in this area.',
  not_working: 'Service is not available in this area yet.'
};

const withTimeout = (promise, timeoutMessage) => (
  Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error(timeoutMessage)), FIRESTORE_TIMEOUT_MS);
    })
  ])
);

const getAreaDocumentId = (name, city) => (
  `${name || ''}-${city || ''}`
    .trim()
    .toLowerCase()
    .replace(/[\\/]+/g, '-')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
);

const toNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeServiceArea = (areaId, areaData = {}) => ({
  id: areaId,
  name: areaData.name || '',
  city: areaData.city || '',
  state: areaData.state || areaData.province || '',
  country: areaData.country || 'India',
  status: areaData.status === 'not_working' ? 'not_working' : 'working',
  message: areaData.message || defaultMessages[areaData.status === 'not_working' ? 'not_working' : 'working'],
  latitude: areaData.latitude ?? '',
  longitude: areaData.longitude ?? '',
  radiusKm: areaData.radiusKm ?? '',
  sortOrder: Number(areaData.sortOrder || 0),
  isActive: areaData.isActive !== false,
  createdAt: areaData.createdAt || null,
  updatedAt: areaData.updatedAt || null
});

const sortServiceAreas = (items) => (
  [...items].sort((left, right) => {
    if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
    return `${left.city} ${left.name}`.localeCompare(`${right.city} ${right.name}`);
  })
);

const formatFirebaseError = (error, fallbackMessage) => {
  const errorCode = error?.code ? ` (${error.code})` : '';
  const errorMessage = error?.message || fallbackMessage;
  return `${fallbackMessage}${errorCode}: ${errorMessage}`;
};

const getStatusLabel = (status) => (
  status === 'not_working' ? 'Not Working' : 'Working'
);

const AdminServiceAreas = () => {
  const [areas, setAreas] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formError, setFormError] = useState('');
  const [syncError, setSyncError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const loadServiceAreas = async () => {
    setIsLoading(true);
    setSyncError('');

    try {
      await ensureFirebaseAuth();

      const snapshot = await withTimeout(
        getDocs(collection(db, SERVICE_AREAS_COLLECTION)),
        'Timed out while loading service areas from Firestore.'
      );

      const loadedAreas = snapshot.docs.map((areaDoc) => (
        normalizeServiceArea(areaDoc.id, areaDoc.data())
      ));

      setAreas(sortServiceAreas(loadedAreas));
    } catch (error) {
      console.error('Failed to load service areas from Firebase', error);
      setAreas([]);
      setSyncError(formatFirebaseError(
        error,
        'Could not load service areas from Firestore'
      ));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadServiceAreas();
  }, []);

  useEffect(() => {
    if (!syncMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setSyncMessage(''), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [syncMessage]);

  const stats = useMemo(() => (
    areas.reduce((totals, area) => {
      totals.total += 1;
      if (area.isActive) totals.active += 1;
      if (area.status === 'working') totals.working += 1;
      if (area.status === 'not_working') totals.notWorking += 1;
      return totals;
    }, { total: 0, active: 0, working: 0, notWorking: 0 })
  ), [areas]);

  const filteredAreas = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return areas.filter((area) => {
      const searchableText = `${area.name} ${area.city} ${area.state} ${area.country} ${area.message}`.toLowerCase();
      const matchesQuery = !normalizedQuery || searchableText.includes(normalizedQuery);
      const matchesStatus = statusFilter === 'all'
        || area.status === statusFilter
        || (statusFilter === 'inactive' && !area.isActive);

      return matchesQuery && matchesStatus;
    });
  }, [areas, searchQuery, statusFilter]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setFormError('');
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'status' && !current.message ? { message: defaultMessages[value] } : {})
    }));
  };

  const buildSearchKeywords = (area) => (
    Array.from(
      new Set(
        [area.name, area.city, area.state, area.country]
          .join(' ')
          .toLowerCase()
          .split(/\s+/)
          .map((keyword) => keyword.trim())
          .filter(Boolean)
      )
    )
  );

  const buildPayload = (areaId, existingArea) => {
    const status = formData.status === 'not_working' ? 'not_working' : 'working';
    const payload = {
      id: areaId,
      name: formData.name.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      province: formData.state.trim(),
      country: 'India',
      status,
      statusLabel: getStatusLabel(status),
      message: formData.message.trim() || defaultMessages[status],
      latitude: toNumberOrNull(formData.latitude),
      longitude: toNumberOrNull(formData.longitude),
      radiusKm: toNumberOrNull(formData.radiusKm),
      sortOrder: Number(formData.sortOrder || 0),
      isActive: Boolean(formData.isActive),
      updatedAt: serverTimestamp()
    };

    return {
      ...payload,
      searchKeywords: buildSearchKeywords(payload),
      ...(existingArea?.createdAt ? { createdAt: existingArea.createdAt } : { createdAt: serverTimestamp() })
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setSyncError('');

    if (!formData.name.trim() || !formData.city.trim()) {
      setFormError('Area name and city are required.');
      return;
    }

    const areaDocumentId = getAreaDocumentId(formData.name, formData.city);
    if (!areaDocumentId) {
      setFormError('Area name is not valid for a Firebase document ID.');
      return;
    }

    const duplicateArea = areas.find((area) => (
      area.id === areaDocumentId && area.id !== editingId
    ));

    if (duplicateArea) {
      setFormError('This area already exists. Edit the existing row instead.');
      return;
    }

    setIsSaving(true);

    try {
      await ensureFirebaseAuth();

      const existingArea = editingId ? areas.find((area) => area.id === editingId) : null;
      const payload = buildPayload(areaDocumentId, existingArea);

      if (editingId && editingId !== areaDocumentId) {
        const batch = writeBatch(db);
        batch.delete(doc(db, SERVICE_AREAS_COLLECTION, editingId));
        batch.set(doc(db, SERVICE_AREAS_COLLECTION, areaDocumentId), payload);
        await withTimeout(
          batch.commit(),
          'Timed out while renaming the service area in Firestore.'
        );
      } else {
        await withTimeout(
          setDoc(doc(db, SERVICE_AREAS_COLLECTION, areaDocumentId), payload),
          editingId
            ? 'Timed out while updating the service area in Firestore.'
            : 'Timed out while creating the service area in Firestore.'
        );
      }

      const savedArea = normalizeServiceArea(areaDocumentId, {
        ...payload,
        createdAt: existingArea?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      setAreas((current) => sortServiceAreas([
        ...current.filter((area) => area.id !== editingId && area.id !== areaDocumentId),
        savedArea
      ]));
      setSyncMessage('Service area synced to Firebase.');
      resetForm();
    } catch (error) {
      console.error('Failed to save service area to Firebase', error);
      setSyncError(formatFirebaseError(
        error,
        'Could not save the service area to Firestore'
      ));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (area) => {
    setEditingId(area.id);
    setFormData({
      name: area.name,
      city: area.city,
      state: area.state,
      status: area.status,
      message: area.message,
      latitude: area.latitude ?? '',
      longitude: area.longitude ?? '',
      radiusKm: area.radiusKm ?? '',
      sortOrder: String(area.sortOrder || 0),
      isActive: area.isActive
    });
    setFormError('');
    setSyncError('');
  };

  const handleDelete = async (areaId) => {
    const area = areas.find((item) => item.id === areaId);
    if (!area) return;

    const shouldDelete = window.confirm(`Delete service area "${area.name}"?`);
    if (!shouldDelete) return;

    setSyncError('');
    setIsSaving(true);

    try {
      await ensureFirebaseAuth();
      await withTimeout(
        deleteDoc(doc(db, SERVICE_AREAS_COLLECTION, areaId)),
        'Timed out while deleting the service area from Firestore.'
      );
      setAreas((current) => current.filter((item) => item.id !== areaId));
      if (editingId === areaId) {
        resetForm();
      }
      setSyncMessage('Service area deleted from Firebase.');
    } catch (error) {
      console.error('Failed to delete service area from Firebase', error);
      setSyncError(formatFirebaseError(
        error,
        'Could not delete the service area from Firestore'
      ));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-service-areas">
      <div className="service-areas-header">
        <div>
          <h1>Service Areas</h1>
          <p>Add working and not-working India locations for the app through Firebase Firestore.</p>
        </div>

        <button
          type="button"
          className="area-btn area-btn-secondary"
          onClick={loadServiceAreas}
          disabled={isLoading || isSaving}
        >
          <RefreshCw size={18} />
          Reload
        </button>
      </div>

      {syncError ? <div className="area-banner error">{syncError}</div> : null}
      {syncMessage ? <div className="area-banner success">{syncMessage}</div> : null}

      <div className="area-stats-grid">
        <div className="area-stat-card">
          <div className="area-stat-icon blue">
            <MapPin size={21} />
          </div>
          <div>
            <span>Total Areas</span>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="area-stat-card">
          <div className="area-stat-icon green">
            <Truck size={21} />
          </div>
          <div>
            <span>Working</span>
            <strong>{stats.working}</strong>
          </div>
        </div>
        <div className="area-stat-card">
          <div className="area-stat-icon rose">
            <MapPin size={21} />
          </div>
          <div>
            <span>Not Working</span>
            <strong>{stats.notWorking}</strong>
          </div>
        </div>
        <div className="area-stat-card">
          <div className="area-stat-icon amber">
            <Save size={21} />
          </div>
          <div>
            <span>Active Rules</span>
            <strong>{stats.active}</strong>
          </div>
        </div>
      </div>

      <div className="area-layout">
        <section className="area-form-panel">
          <div className="area-section-head">
            <h2>{editingId ? 'Edit Location' : 'Add Location'}</h2>
              <p>Customer app can read this collection to show India service availability UI.</p>
          </div>

          <form className="area-form" onSubmit={handleSubmit}>
            <label className="area-field">
              <span>Area Name</span>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Andheri West"
                disabled={isLoading || isSaving}
              />
            </label>

            <div className="area-grid two">
              <label className="area-field">
                <span>City</span>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="e.g. Mumbai"
                  disabled={isLoading || isSaving}
                />
              </label>

              <label className="area-field">
                <span>State</span>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="e.g. Maharashtra"
                  disabled={isLoading || isSaving}
                />
              </label>
            </div>

            <label className="area-field">
              <span>Service Status</span>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isLoading || isSaving}
              >
                <option value="working">Working Area</option>
                <option value="not_working">Not Working Area</option>
              </select>
            </label>

            <label className="area-field">
              <span>App Message</span>
              <textarea
                name="message"
                rows="3"
                value={formData.message}
                onChange={handleChange}
                placeholder={defaultMessages[formData.status]}
                disabled={isLoading || isSaving}
              />
            </label>

            <div className="area-grid three">
              <label className="area-field">
                <span>Latitude</span>
                <input
                  type="number"
                  step="any"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  placeholder="24.8607"
                  disabled={isLoading || isSaving}
                />
              </label>
              <label className="area-field">
                <span>Longitude</span>
                <input
                  type="number"
                  step="any"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  placeholder="67.0011"
                  disabled={isLoading || isSaving}
                />
              </label>
              <label className="area-field">
                <span>Radius (km)</span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  name="radiusKm"
                  value={formData.radiusKm}
                  onChange={handleChange}
                  placeholder="5"
                  disabled={isLoading || isSaving}
                />
              </label>
            </div>

            <div className="area-grid two align-end">
              <label className="area-field">
                <span>Sort Order</span>
                <input
                  type="number"
                  name="sortOrder"
                  value={formData.sortOrder}
                  onChange={handleChange}
                  disabled={isLoading || isSaving}
                />
              </label>

              <label className="area-check-field">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  disabled={isLoading || isSaving}
                />
                <span>Active in app</span>
              </label>
            </div>

            {formError ? <div className="area-form-error">{formError}</div> : null}

            <div className="area-form-actions">
              <button
                type="button"
                className="area-btn area-btn-secondary"
                onClick={resetForm}
                disabled={isLoading || isSaving}
              >
                Clear
              </button>
              <button
                type="submit"
                className="area-btn area-btn-primary"
                disabled={isLoading || isSaving}
              >
                {editingId ? <Save size={18} /> : <Plus size={18} />}
                {isSaving ? 'Saving...' : (editingId ? 'Update Area' : 'Add Area')}
              </button>
            </div>
          </form>
        </section>

        <section className="area-list-panel">
          <div className="area-section-head">
            <h2>Location Rules</h2>
            <p>Firestore path: {SERVICE_AREAS_COLLECTION}</p>
          </div>

          <div className="area-toolbar">
            <input
              type="text"
              className="area-search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search area, city, state..."
              disabled={isLoading}
            />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              disabled={isLoading}
            >
              <option value="all">All Areas</option>
              <option value="working">Working</option>
              <option value="not_working">Not Working</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {isLoading ? (
            <div className="area-empty-state">
              <RefreshCw size={44} className="area-spin" />
              <h3>Loading service areas</h3>
              <p>Fetching location rules from Firestore.</p>
            </div>
          ) : filteredAreas.length ? (
            <div className="area-table-wrap">
              <table className="area-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Coverage</th>
                    <th>App Message</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAreas.map((area) => (
                    <tr key={area.id}>
                      <td>
                        <div className="area-location-cell">
                          <strong>{area.name}</strong>
                          <span>{[area.city, area.state, area.country].filter(Boolean).join(', ') || '-'}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`area-status ${area.status}`}>
                          {getStatusLabel(area.status)}
                        </span>
                      </td>
                      <td>
                        <div className="area-coverage-cell">
                          <span>{area.radiusKm ? `${area.radiusKm} km radius` : 'Manual area rule'}</span>
                          <small>
                            {area.latitude && area.longitude
                              ? `${area.latitude}, ${area.longitude}`
                              : 'No coordinates'}
                          </small>
                        </div>
                      </td>
                      <td>{area.message}</td>
                      <td>
                        <span className={`area-active-badge ${area.isActive ? 'active' : 'inactive'}`}>
                          {area.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="area-actions">
                          <button
                            type="button"
                            className="area-icon-btn"
                            onClick={() => handleEdit(area)}
                            disabled={isSaving}
                            title="Edit location"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            type="button"
                            className="area-icon-btn danger"
                            onClick={() => handleDelete(area.id)}
                            disabled={isSaving}
                            title="Delete location"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="area-empty-state">
              <MapPin size={44} />
              <h3>No service areas found</h3>
              <p>Add a working or not-working location to sync the first rule to Firebase.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminServiceAreas;
